import { CareEnvironment, CarePhase } from '../appModel';
import { getFirstPayoutMonthIndex } from '../calculations/funding/projection/projectionTotals';

export type WalkthroughMonthStatus = 'beforeTrigger' | 'waitingPeriod' | 'paying' | 'notPaying';

export interface WalkthroughMonth {
  monthIndex: number;
  carePhase: CarePhase;
  careEnvironment: CareEnvironment;
  careCost: number;
  paidByInsurance: number;
  stillNeeded: number;
  status: WalkthroughMonthStatus;
}

export interface WalkthroughSummary {
  months: number;
  totalCareCost: number;
  paidByInsurance: number;
  stillNeeded: number;
  remainingDeathBenefit: number;
}

function monthStatus(month: MonthlyEntry): WalkthroughMonthStatus {
  switch (month.events.ineligibilityReason) {
    case 'lt-2-adl':
    case 'pre-policy':
      return 'beforeTrigger';
    case 'in-wp':
      return 'waitingPeriod';
    case null:
      return 'paying';
    default:
      return 'notPaying';
  }
}

function monthOutOfPocket(month: MonthlyEntry): number {
  const roomAndBoardOutOfPocket =
    (month.facts.roomAndBoardCost ?? 0) - (month.deltas.payout.roomAndBoardCovered ?? 0);
  return Math.max(0, month.deltas.payout.ineligibleCost + roomAndBoardOutOfPocket);
}

/**
 * Replays a policy's monthly claim projection as care bills: insurance pays its share and the
 * rest is still needed that month. A bill can arrive before insurance pays, even when totals
 * look sufficient, because insurance pays only after its trigger and waiting period.
 */
export function buildCareWalkthrough(projection: ClaimProjection): WalkthroughMonth[] {
  return projection.months.map((month, monthIndex) => {
    const careCost = month.facts.effectiveCareCost + (month.facts.roomAndBoardCost ?? 0);
    const outOfPocket = Math.min(careCost, monthOutOfPocket(month));

    return {
      monthIndex,
      carePhase: month.context.carePhase,
      careEnvironment: month.context.careEnvironment,
      careCost,
      paidByInsurance: careCost - outOfPocket,
      stillNeeded: outOfPocket,
      status: monthStatus(month),
    };
  });
}

/**
 * The key moments of a care event, as month indexes: care begins, insurance starts paying,
 * moderate care begins and full care begins. Moments that never happen are skipped.
 */
export function walkthroughMomentIndexes(projection: ClaimProjection): number[] {
  const { months } = projection;
  const firstMonthOfPhase = (phase: CarePhase) =>
    months.findIndex(month => month.context.carePhase === phase);

  const moments = [
    0,
    getFirstPayoutMonthIndex(months),
    firstMonthOfPhase(CarePhase.moderateCare),
    firstMonthOfPhase(CarePhase.fullCare),
  ].filter((index): index is number => index !== null && index >= 0 && index < months.length);

  return [...new Set(moments)].sort((a, b) => a - b);
}

export function summarizeWalkthrough(
  walkthrough: WalkthroughMonth[],
  projection: ClaimProjection,
): WalkthroughSummary {
  const total = (pick: (month: WalkthroughMonth) => number) =>
    walkthrough.reduce((sum, month) => sum + pick(month), 0);
  const lastMonth = projection.months[projection.months.length - 1];

  return {
    months: walkthrough.length,
    totalCareCost: total(month => month.careCost),
    paidByInsurance: total(month => month.paidByInsurance),
    stillNeeded: total(month => month.stillNeeded),
    remainingDeathBenefit: lastMonth?.state.pools.deathBenefit ?? 0,
  };
}
